import { useEffect, useState } from "react";
import { api } from "../../../../lib/api";
import SearchableSelect from "./SearchableSelect";

/**
 * Searchable selector that shows the user's real, meaningful accounts -
 * Cash, and each Active Accounting Bank Account formatted "Bank — Account
 * name — ****1234" (the account number arrives already masked from the
 * backend) - instead of exposing the internal Ledger name (e.g. "Main
 * bank") that a generic ledger dropdown would show. See the "Correct Bank
 * Selection in Voucher Screens" requirement for the full rationale.
 *
 * Phase 5 "Searchable Selectors" correction: typing "IDBI" (or the branch,
 * account name, or any digits of the masked number) filters straight to
 * that account instead of scrolling a long list - the plain <Select> this
 * used before is not acceptable once a Society has more than a few accounts.
 *
 * The selected value is encoded as "bank:<bankAccountId>" or
 * "ledger:<ledgerId>" (Cash only) so callers can build a voucher line with
 * exactly one of bankAccountId/ledgerId - never both, and never a ledger id
 * the browser invented for a bank-backed option. Decode with
 * decodeBankAccountSelection() before submitting.
 */
export function decodeBankAccountSelection(value) {
  if (!value) return null;
  const sep = value.indexOf(":");
  if (sep < 0) return null;
  const kind = value.slice(0, sep);
  const id = value.slice(sep + 1);
  if (kind === "bank") return { bankAccountId: id };
  if (kind === "ledger") return { ledgerId: id };
  return null;
}

export default function BankAccountSelect({ value, onChange, placeholder = "Search Cash or a Bank Account...", excludeValue, disabled }) {
  const [bankAccounts, setBankAccounts] = useState([]);
  const [cashLedgers, setCashLedgers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [accountsRes, ledgersRes] = await Promise.all([
          api.get("/society/accounting/engine/bank-accounts").catch(() => ({ data: [] })),
          api.get("/society/accounting/engine/ledgers").catch(() => ({ data: [] })),
        ]);
        setBankAccounts(Array.isArray(accountsRes.data) ? accountsRes.data : []);
        setCashLedgers((Array.isArray(ledgersRes.data) ? ledgersRes.data : []).filter((l) => l.ledgerGroup === "cash"));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const options = [
    ...cashLedgers.map((l) => ({ value: `ledger:${l.id}`, label: l.name, group: "Cash", keywords: ["cash"] })),
    ...bankAccounts.map((a) => ({
      value: `bank:${a.id}`,
      label: [a.bankName, a.accountName, a.accountNumber].filter(Boolean).join(" — "),
      group: "Bank Accounts",
      keywords: [a.bankName, a.branch, a.accountName, a.accountNumber],
    })),
  ].filter((opt) => opt.value !== excludeValue);

  return (
    <SearchableSelect
      value={value}
      onChange={onChange}
      options={options}
      loading={loading}
      disabled={disabled}
      placeholder={placeholder}
      searchPlaceholder="Search bank, branch, account name or number..."
      emptyText="No Cash ledger or Bank Accounts set up yet."
    />
  );
}
